import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PaymentDeviceDtoIn } from '@/models/in/PaymentDeviceDtoIn';
import { Field, inputClassName } from './dialogParts';

interface DevicePickerFieldProps {
  id: string;
  label: string;
  devices: PaymentDeviceDtoIn[];
  value: number | null;
  onChange: (deviceId: number | null) => void;
  isLoading?: boolean;
}

/**
 * Search-and-pick of a free device, drawn inline like FundPickerField (a portal list is out of reach
 * inside a modal dialog). The whole list shows from the start and narrows as the office types a
 * device number - or a word from its notes.
 */
const DevicePickerField = ({ id, label, devices, value, onChange, isLoading = false }: DevicePickerFieldProps) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');

  const selected = value != null ? devices.find((d) => d.id === value) : null;

  const matches = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return devices;
    return devices.filter((d) => `${d.deviceNumber} ${d.notes ?? ''}`.toLowerCase().includes(query));
  }, [devices, search]);

  if (selected) {
    return (
      <Field id={id} label={label}>
        <div className="flex items-center justify-between gap-3 border border-(--border-main) rounded-lg px-3 py-2">
          <span id={id} className="body-sm-regular">
            <bdi className="body-sm-semibold">{selected.deviceNumber}</bdi>
            {selected.notes ? ` · ${selected.notes}` : ''}
          </span>
          <button type="button" className="body-sm-semibold text-(--font-link)" onClick={() => { onChange(null); setSearch(''); }}>
            {t('paymentDevices.dialogs.changeDevice')}
          </button>
        </div>
      </Field>
    );
  }

  return (
    <Field id={id} label={label}>
      <input
        id={id}
        type="text"
        className={inputClassName}
        value={search}
        placeholder={t('paymentDevices.dialogs.searchDevice')}
        onChange={(e) => setSearch(e.target.value)}
        autoComplete="off"
        disabled={isLoading}
      />
      {isLoading ? (
        <p className="body-sm-regular text-(--font-tertiary)">{t('loading')}...</p>
      ) : matches.length === 0 ? (
        <p className="body-sm-regular text-(--font-tertiary)">{t('paymentDevices.dialogs.noDevicesFound')}</p>
      ) : (
        <ul className="border border-(--border-main) rounded-lg max-h-[240px] overflow-y-auto custom-scrollbar">
          {matches.map((device) => (
            <li key={device.id}>
              <button
                type="button"
                className="w-full text-start px-3 py-2 body-sm-regular hover:bg-(--surface-secondary-lowest)"
                onClick={() => { onChange(device.id); setSearch(''); }}
              >
                <bdi className="body-sm-semibold">{device.deviceNumber}</bdi>
                {device.notes ? <span className="text-(--font-tertiary)"> · {device.notes}</span> : null}
              </button>
            </li>
          ))}
        </ul>
      )}
    </Field>
  );
};

export default DevicePickerField;
